import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { ApplicationService } from '@app/store/application/application.service';
import { QuoteSessionQuery } from '@app/store/session/session.query';
import { QuoteSessionService } from '@app/store/session/session.service';
import { RETRIEVE_TYPES } from '@constants/api-endpoints';
import { API_ERRORS } from '@constants/api-errors';
import { ROUTES } from '@constants/routes';
import { QuoteSession } from '@models/QuoteSession';
import { AnalyticsService } from '@services/analytics.service';
import { OrchestratorService } from '@services/orchestrator/orchestrator.service';
import { UtmService } from '@services/utm.service';
import { firstValueFrom, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';

@Component({
	standalone: false,
	selector: 'qe-retrieve',
	templateUrl: './retrieve.component.html',
	styleUrls: ['./retrieve.component.scss'],
})
export class RetrieveComponent implements OnInit, OnDestroy  {
	public readonly afterYouEnroll: string[] = [
		`Embrace will request your pet's medical records from your Vet`,
		`After your waiting period, your pet is covered`,
		`Rest assured coverage will be there when you need it`,
	];
	public quoteRetrievalForm!: FormGroup;
	public formInvalid: boolean = false;
	private readonly ngUnsubscribe$: Subject<boolean> = new Subject();
	private readonly zipLength: number = 5;
	private params!: ParamMap;
	public get zipCode(): AbstractControl | null {
		return this.quoteRetrievalForm.get(`zipCode`);
	}
	public get emailAddress(): AbstractControl | null {
		return this.quoteRetrievalForm.get(`emailAddress`);
	}
	/** Gets the zip code from the query params */
	private get zipCodeParamValue(): string {
		return (this.params?.get('zipCode') || this.params?.get('zip') || this.params?.get('zip_code')) ?? '';
	}

	constructor(
		private readonly route: ActivatedRoute,
		private readonly router: Router,
		private readonly fb: FormBuilder,
		private readonly titleService: Title,
		private readonly sessionActions: QuoteSessionService,
		private readonly appActions: ApplicationService,
		private readonly sessionQuery: QuoteSessionQuery,
		private readonly orchestratorService: OrchestratorService,
		private readonly utmService: UtmService,
		private readonly gtmService: AnalyticsService,
	) {
		this.route.queryParamMap
			.pipe(takeUntil(this.ngUnsubscribe$))
			.subscribe(params => {
				if (params.keys.length) {
					this.params = params;
				}
			});

		this.quoteRetrievalForm = this.fb.group({
			zipCode: [
				this.zipCodeParamValue,
				{
					validators: [
						Validators.required,
						Validators.minLength(this.zipLength),
						Validators.maxLength(this.zipLength),
						Validators.pattern(/^\d*$/im),
					],
					updateOn: `blur`,
				},
			],
			emailAddress: [
				'',
				[
					Validators.required,
					Validators.email,
				],
			],
		}, { updateOn: `blur` });

		this.quoteRetrievalForm.valueChanges
			.pipe(takeUntil(this.ngUnsubscribe$))
			.subscribe(() => {
				if (this.formInvalid) {
					this.formInvalid = false;
					this.quoteRetrievalForm.updateValueAndValidity();
				}
			});

		this.sessionQuery.contactState$()
			.pipe(
				takeUntil(this.ngUnsubscribe$),
				filter(contact => !!contact),
			).subscribe((contact) => {
				if (contact.emailAddress) this.emailAddress?.patchValue(contact.emailAddress);
				if (contact.ratingZipCode) this.zipCode?.patchValue(contact.ratingZipCode);
			});

		const retrieveTypes: string[] = [RETRIEVE_TYPES.WEB, RETRIEVE_TYPES.SMS];
		// if retrieveType is undefined, i.e. address is `/retrieve`, then set retrieveType to 'web'
		const retrieveTypeParam = this.route.snapshot.params.retrieveType || RETRIEVE_TYPES.WEB;

		// if retrieveType is not 'web' or 'sms' throw error page
		if (!retrieveTypes.includes(retrieveTypeParam.toLowerCase())){
			this.router.navigate(
				[ROUTES.ERROR],
				{
					state: {
						detail: `This page does not exist`,
					},
				},
			);
		}
	}


	async ngOnInit(): Promise<void> {
		this.titleService.setTitle(`Retrieve Your Quote | Embrace QuoteEngine`);

		const utmParams = this.utmService.getUtmCookie();
		this.gtmService.push({
			event: 'checkout',
			pagePath: 'retrieve',
			utm_source: utmParams.source,
			utm_medium: utmParams.medium,
			utm_term: utmParams.term,
			utm_campaign: utmParams.campaign
		});
	}


	public async submitForm(): Promise<void> {
		this.quoteRetrievalForm.markAllAsTouched();
		if (this.quoteRetrievalForm.invalid) {
			this.formInvalid = true;

			return;
		}
		this.appActions.setLoading(true);
		const {
			zipCode: ratingZipCode,
			emailAddress,
		} = this.quoteRetrievalForm.getRawValue();

		this.retrieveQuote(emailAddress, ratingZipCode);
	}

	private async retrieveQuote(
		emailAddress: string,
		ratingZipCode: string,
		coverage: string = ''): Promise<void> {
		const analyticsInfo = this.utmService.getUtmCookie();
		// if retrieveType is undefined, i.e. address is `/retrieve`, then set retrieveType to 'web'
		const retrieveType = this.route.snapshot.params.retrieveType?.toLowerCase() || RETRIEVE_TYPES.WEB;

		try {
			const res: QuoteSession = await firstValueFrom(this.orchestratorService.retrieveQuote({
				emailAddress,
				ratingZipCode,
				isWebRetrieve: retrieveType === RETRIEVE_TYPES.WEB,
				analyticsInfo,
				retrieveType,
			}).pipe(

				map(r => (coverage ? firstValueFrom(this.orchestratorService.updatePetCoverage(r.id, coverage, r.pets[0])) : r)),
			));
			this.sessionActions.update(res);

			// check if the session brand matches the brand on the contact
			// and update the contact to the correct brand from the quote if it doesn't
			const sessionBrand = await firstValueFrom(this.sessionQuery.brand$());
			if (sessionBrand.toLowerCase() !== res?.contact?.brand?.toLowerCase()) {
				this.sessionActions.setBrandOnContact(res?.contact?.brand || 'Embrace');
			}

			this.appActions.setLastPetActive();
			this.router.navigate([ROUTES.SELECT_A_POLICY], { queryParams: { rq: '1' } });

			return;
		} catch (error) {
			const err = error as HttpErrorResponse;
			if (err.error) {
				err.error.title ??= ``;
				this.appActions.setLoading(false);
				switch (err.error.title) {
					case API_ERRORS.POLICY_HOLDER_EXISTS:
						this.router.navigate(
							[ROUTES.ERROR],
							{
								state: {
									detail: err.error.detail?.split(` :: `)[1],
								},
							},
						);
						break;
					case API_ERRORS.QUOTE_RETRIEVAL_FAILED:
						this.router.navigate(
							[ROUTES.PET_INFORMATION],
							{
								queryParams: {
									rq: '2',
								},
								state: {
									quoteRetrievalError: true,
								},
								queryParamsHandling: `merge`,
							},
						);
						break;
					default:
						this.router.navigate(
							[ROUTES.ERROR],
							{
								state: {
									// eslint-disable-next-line max-len
									detail: `There was an error retrieving your quote. Please try again shortly. If you continue to have issues please contact us.`,
								},
							},
						);
						break;
				}
			}

			return;
		}
	}


	ngOnDestroy(): void {
		this.ngUnsubscribe$.next(true);
		this.ngUnsubscribe$.unsubscribe();
	}
}
